<template>
  <ak-modal
    v-model:active="isModalActive"
    :submit-form="createFolder"
    size="tiny"
  >
    <ak-modal-body>
      <ak-modal-title>Create Folder</ak-modal-title>
      <ak-field label="Folder Name">
        <ak-input
          v-model="folderName"
          name="folderName"
          required
          :custom-rules="[validationRule]"
        />
      </ak-field>
    </ak-modal-body>
    <ak-modal-footer>
      <ak-modal-button type="submit">Create Folder</ak-modal-button>
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { ref, defineComponent, watch } from 'vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { uniqueFolderValidation } from '../../utils/validationRules'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-folders-sidebar></ak-folders-sidebar>
   */
  name: 'AkCreateFolderModal',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [ModalButton.name]: ModalButton,
    [Modal.name]: Modal,
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
    [ModalTitle.name]: ModalTitle,
    [Field.name]: Field,
    [Input.name]: Input,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Array}
     * @description The list of folders that are on the account.
     */
    foldersList: {
      type: Array,
      default: () => [],
    },

    /**
     * @type {Boolean} Boolean flag to determine if the modal window is
     * active or not.
     */
    isActive: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Function}
     * @description Callback for when the folder has been created
     */
    folderCreated: {
      type: Function,
      default: () => {},
    },
  },

  emits: ['update:isActive'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const isModalActive = ref(props.isActive)
    const folderName = ref('')

    /**
     * @description Checks to see if the new folder name is unique this
     * is executed on input change so that it will make the field as
     * invalid if the folder name exists. This will prevent the form
     * from being submitted.
     */
    const validationRule = uniqueFolderValidation({
      foldersList: () => props.foldersList,
    })

    /**
     * @description Creates a new folder on the active account.
     */
    const createFolder = async () => {
      await props.folderCreated(folderName.value)
      ToastProgrammatic.open({
        message: 'Folder has been created.',
        type: 'success',
      })
    }

    watch(isModalActive, (value) => {
      folderName.value = ''
      emit('update:isActive', value)
    })

    watch(
      () => props.isActive,
      (value) => {
        isModalActive.value = value
      },
    )

    return {
      isModalActive,
      folderName,
      validationRule,
      createFolder,
    }
  },
})
</script>
